import { FsnAcceptOrderDialog } from '@/components/orders/FsnAcceptOrderDialog'
import { FsnOrderChangeReview } from '@/components/orders/FsnOrderChangeReview'
import { FsnRefuseOrderDialog } from '@/components/orders/FsnRefuseOrderDialog'
import { FsnWireInHeaderMeta } from '@/components/orders/FsnWireInHeaderMeta'
import { Can, useAbility } from '@/lib/casl'
import {
  AdminHeaderActionButton,
  AdminPageHeader,
  AdminRecordHeader,
} from '@/components/patterns/admin-page'
import {
  applySuggestedDeliveryAddress,
  buildDeliveryAddressSearchString,
} from '@/components/forms/Orders/fulfillment-destination.helpers'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DetailWell } from '@/components/patterns/detail-page'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { usePathHelper } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import {
  fsnNetworkQueries,
  useCheckFsnIncomingOrderAddressMutation,
  useFsnIncomingOrdersBackgroundCheck,
  useFsnIncomingOrder,
  useFsnIncomingOrders,
  useRefreshFsnIncomingOrdersMutation,
  useRetryFsnIncomingOrderImportMutation,
} from '@/lib/services/fsn-network/fsn-network.query'
import { formatLineItemName } from '@/lib/line-item-display'
import { formatCurrency } from '@/lib/utils'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Inbox,
  Loader2,
  MapPin,
  Package,
  PackageCheck,
  Phone,
  RefreshCw,
  RotateCcw,
  ShoppingCart,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { wireInActionSuccessMessage } from '@/components/orders/fsn-order-action-message'
import { wireInRefreshSuccessMessage } from './-wire-in-refresh-message'

const WIRE_IN_PAGE_SIZE = 25
const dayCountWords = {
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
}

function defaultIncomingOrderFilters() {
  return { page: 1, per_page: WIRE_IN_PAGE_SIZE }
}

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/wire-in')({
  validateSearch: z.object({
    incomingOrderId: z.coerce.number().int().positive().optional(),
  }),
  loaderDeps: ({ search }) => ({ incomingOrderId: search.incomingOrderId }),
  component: WireInOrdersPage,
  loader: async ({ context, params, deps = {} }) => {
    if (!context.ability.can('list', 'sales-orders')) throw notFound()

    const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
    const isReadOnly = !context.ability.can('create', 'sales-orders')
    const incomingOrderId = deps.incomingOrderId ?? null

    if (isGlobalOrg) {
      return { isGlobalOrg: true, isReadOnly, incomingOrderId: null }
    }

    if (incomingOrderId) {
      await context.queryClient.ensureQueryData(
        fsnNetworkQueries.incomingOrder({
          organizationId: params.orgId,
          incomingOrderId,
        })
      )

      return { isGlobalOrg: false, isReadOnly, incomingOrderId }
    }

    await context.queryClient.ensureQueryData(
      fsnNetworkQueries.incomingOrders({
        organizationId: params.orgId,
        filters: defaultIncomingOrderFilters(),
      })
    )

    return { isGlobalOrg: false, isReadOnly, incomingOrderId: null }
  },
})

function WireInOrdersPage() {
  const { isGlobalOrg = false, isReadOnly = false, incomingOrderId = null } = Route.useLoaderData()

  if (isGlobalOrg) {
    return <WireInOrdersGlobalOrgPage />
  }

  if (incomingOrderId) {
    return <FocusedWireInOrderPage incomingOrderId={incomingOrderId} isReadOnly={isReadOnly} />
  }

  return <WireInOrdersFloristPage isReadOnly={isReadOnly} />
}

function WireInOrdersGlobalOrgPage() {
  return (
    <div className="space-y-5 pb-8">
      <AdminPageHeader
        icon={Inbox}
        title="Orders Waiting"
        description="Accept or refuse incoming Flower Shop Network orders."
      />

      <OrganizationContextNotice>
        Flower Shop Network orders are florist-specific. Impersonate a florist organization to
        review or handle them.
      </OrganizationContextNotice>
    </div>
  )
}

function WireInOrdersFloristPage({ isReadOnly = false }) {
  const { orgId } = Route.useParams()
  const [showHandled, setShowHandled] = useState(false)
  const [page, setPage] = useState(1)
  // Orders handled on this visit stay in place, updated, until the list is left or refreshed,
  // so staff can open the order they just accepted.
  const [handledOrders, setHandledOrders] = useState([])
  const changePage = (nextPage) => {
    setHandledOrders([])
    setPage(nextPage)
  }
  const filters = {
    ...defaultIncomingOrderFilters(),
    page,
    ...(showHandled ? { include_handled: 1 } : {}),
  }
  const { data: inbox } = useFsnIncomingOrders(filters)
  const refreshMutation = useRefreshFsnIncomingOrdersMutation()
  const orders = inbox?.data ?? []
  const visibleOrders = keepHandledInPlace(orders, handledOrders)
  const keepHandled = (updated) => {
    if (!updated?.id) return
    const index = visibleOrders.findIndex((order) => order.id === updated.id)
    setHandledOrders((current) => [
      ...current.filter((entry) => entry.order.id !== updated.id),
      { order: updated, index: index < 0 ? visibleOrders.length : index },
    ])
  }
  const meta = inbox?.meta ?? {
    current_page: page,
    page_size: WIRE_IN_PAGE_SIZE,
    total: orders.length,
    last_page: 1,
  }
  const summary = inbox?.summary ?? {}
  const refusalReasons = inbox?.refusal_reasons ?? {}
  const contactMessages = uniqueContactMessages(orders)
  const isSampleIntegration = inbox?.integration?.sample === true
  const canCheckForNewWireInOrders = Boolean(
    inbox?.integration?.incoming_orders_enabled ?? inbox?.integration?.enabled
  )
  const backgroundCheck = useFsnIncomingOrdersBackgroundCheck({
    enabled: canCheckForNewWireInOrders,
  })
  const isChecking = refreshMutation.isPending || backgroundCheck.isFetching

  const refresh = () => {
    refreshMutation.mutate(undefined, {
      onSuccess: (response) => {
        changePage(1)
        toast.success(wireInRefreshSuccessMessage(response))
      },
      onError: (error) =>
        toast.error(error?.response?.data?.message ?? 'Unable to check for new orders.'),
    })
  }

  return (
    <div className="space-y-5 pb-8">
      <AdminPageHeader
        icon={Inbox}
        title="Orders Waiting"
        description={
          <WireInHeaderDescription
            messages={contactMessages}
            canCheckForNewOrders={canCheckForNewWireInOrders}
          />
        }
        actions={
          <AdminHeaderActionButton
            onClick={refresh}
            disabled={isChecking || !canCheckForNewWireInOrders}
            title={
              canCheckForNewWireInOrders
                ? undefined
                : 'Turn on incoming F2F orders before checking for new orders.'
            }
          >
            {isChecking ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            Check for new orders
          </AdminHeaderActionButton>
        }
      />

      {!canCheckForNewWireInOrders ? (
        <Alert variant={isSampleIntegration ? 'info' : 'warning'}>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {isSampleIntegration
              ? 'Sample FSN order history'
              : 'Incoming F2F checks are turned off'}
          </AlertTitle>
          <AlertDescription>
            {isSampleIntegration
              ? 'This demo never contacts Flower Shop Network. Checking for new orders is turned off, but the completed sample order remains available to explore.'
              : 'This shop is not checking Flower Shop Network for new orders. Orders already here can still be accepted, refused, or retried.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {Number(summary.unhandled ?? 0) > 0 ? (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Action required</AlertTitle>
          <AlertDescription>
            {Number(summary.unhandled) === 1
              ? '1 order needs attention.'
              : `${summary.unhandled} orders need attention.`}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Typography variant="supporting">
          {/* Kept handled orders count as shown until they leave the list. */}
          {wireInOrderCountText(
            { ...meta, total: Number(meta.total ?? 0) + visibleOrders.length - orders.length },
            visibleOrders.length
          )}
        </Typography>
        <label className="flex items-center gap-2 text-sm font-medium">
          <Switch
            checked={showHandled}
            onCheckedChange={(checked) => {
              setShowHandled(checked)
              changePage(1)
            }}
            aria-label="Show handled orders"
          />
          Show handled
          {Number(summary.handled ?? 0) > 0 ? (
            <span className="text-muted-foreground">({summary.handled})</span>
          ) : null}
        </label>
      </div>

      <div className="space-y-3">
        {visibleOrders.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <Typography variant="supporting">No orders are waiting right now.</Typography>
            </CardContent>
          </Card>
        ) : (
          visibleOrders.map((order) => (
            <WireInOrderCard
              key={order.id}
              order={order}
              refusalReasons={refusalReasons}
              orgId={orgId}
              isReadOnly={isReadOnly}
              onOrderHandled={keepHandled}
            />
          ))
        )}
      </div>

      <WireInPagination meta={meta} onPageChange={changePage} />
    </div>
  )
}

function FocusedWireInOrderPage({ incomingOrderId, isReadOnly = false }) {
  const { orgId } = Route.useParams()
  const path = usePathHelper()
  const { data: order } = useFsnIncomingOrder(incomingOrderId)

  return (
    <div className="max-w-5xl space-y-5 pb-8">
      <AdminPageHeader
        icon={Inbox}
        title="Orders Waiting"
        actions={
          <Button asChild variant="outline">
            <Link {...path.wireIn.index().using({ orgId }).link} search={{}}>
              <ChevronLeft className="h-4 w-4" />
              Back to all
            </Link>
          </Button>
        }
      />

      {order ? (
        <WireInOrderCard
          order={order}
          refusalReasons={order.refusal_reasons ?? {}}
          orgId={orgId}
          isReadOnly={isReadOnly}
        />
      ) : null}
    </div>
  )
}

function WireInOrderCard(props) {
  const change = props.order.change_request
  if (change) {
    return <FsnOrderChangeReview key={`${change.state}-${change.id}`} {...props} />
  }
  return <NewWireInOrderCard {...props} />
}

function NewWireInOrderCard({ order, refusalReasons, orgId, isReadOnly, onOrderHandled }) {
  const { formatPhone } = usePhoneFormatter()
  const { geocodeAddress } = usePlacesAutocomplete()
  const addressCheckMutation = useCheckFsnIncomingOrderAddressMutation()
  const retryMutation = useRetryFsnIncomingOrderImportMutation()
  const [addressCheck, setAddressCheck] = useState(null)
  const [isCheckingAddress, setIsCheckingAddress] = useState(false)
  const products = order.summary?.products ?? []
  const recipient = order.summary?.recipient ?? {}
  const deliveryDate = formatDeliveryDate(order.delivery_date)
  const relativeDeliveryDate = formatRelativeDeliveryDate(order.delivery_date)
  const recipientAddress = getRecipientAddress(recipient, order)
  const recipientAddressLines = formatRecipientAddressLines(recipientAddress)
  const mapsUrl = buildMapsUrl(recipientAddress)
  const addressStatus = getAddressStatus(recipientAddress)
  const recipientPhone = formatRecipientPhone(order, recipient, formatPhone)
  // Responding is its own permission; retrying an import creates the OneStem order.
  const canRespond = useAbility().can('respond', 'wire-in-orders')
  // Refused or ended in FSN: nothing is owed, whatever was expected before.
  const noPayout = ['refused', 'handled_in_fsn'].includes(order.status)
  // An imported order's resend notes belong to its change review.
  const resendNote = order.sales_order_id ? null : order.summary?.resend_message
  const hasActions =
    order.sales_order_id ||
    (!isReadOnly && order.can_retry_import) ||
    (canRespond && (order.can_accept || order.can_refuse))
  const canCheckAddress = recipientAddressLines.length > 0

  const checkAddress = async () => {
    const baseAddress = createDeliveryAddressFromRecipient(recipientAddress)
    const addressText = buildDeliveryAddressSearchString(baseAddress)

    if (!addressText || addressText.length < 5) {
      setAddressCheck({ status: 'incomplete' })
      return
    }

    setIsCheckingAddress(true)

    try {
      const suggestedAddress = await geocodeAddress(addressText)

      if (!suggestedAddress) {
        setAddressCheck({ status: 'not_found' })
        return
      }

      const verifiedAddress = {
        ...applySuggestedDeliveryAddress(baseAddress, suggestedAddress),
        formatted_address: suggestedAddress.formattedAddress || null,
      }
      const response = await addressCheckMutation.mutateAsync({
        incomingOrderId: order.id,
        address: verifiedAddress,
      })

      setAddressCheck({
        status: 'checked',
        address: response?.address ?? verifiedAddress,
        serviceability: response?.serviceability ?? null,
      })
    } catch (error) {
      setAddressCheck({ status: 'error' })
      toast.error(error?.response?.data?.message ?? 'Unable to check this address right now.')
    } finally {
      setIsCheckingAddress(false)
    }
  }

  const mutateWithToast = (mutation, variables, messages) => {
    mutation.mutate(variables, {
      onSuccess: (response) => {
        toast.success(wireInActionSuccessMessage(response, messages.success))
        onOrderHandled?.(response)
      },
      onError: (error) => toast.error(error?.response?.data?.message ?? messages.error),
    })
  }

  return (
    <AdminRecordHeader
      icon={ShoppingCart}
      iconTone="brand"
      title={
        order.sales_order_id
          ? `Order #${order.sales_order_id}`
          : order.status === 'pending'
            ? 'New FSN order'
            : 'FSN order'
      }
      titleTrailing={<StatusBadge status={order.status} fsnStatus={order.fsn_status} />}
      meta={<FsnWireInHeaderMeta order={order} />}
      layout="workspace"
      className="@container/wire-in"
      after={
        <>
          <div className="space-y-4 p-5">
            {order.last_error ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Needs attention</AlertTitle>
                <AlertDescription>{order.last_error}</AlertDescription>
              </Alert>
            ) : null}

            <div className="grid gap-3 @[48rem]/wire-in:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.35fr)]">
              <DecisionDetail
                label="Expected payout"
                value={noPayout ? 'No payout' : money(order.expected_payout)}
                supportingValue={
                  order.order_value ? `Order value ${money(order.order_value)}` : null
                }
                accent={noPayout ? 'neutral' : 'mint'}
              />
              <DecisionDetail
                icon={CalendarDays}
                label="Delivery date"
                value={deliveryDate}
                supportingValue={relativeDeliveryDate}
                accent="periwinkle"
              />
              <AddressDecisionDetail
                addressLines={recipientAddressLines}
                mapsUrl={mapsUrl}
                status={addressStatus}
                addressCheck={addressCheck}
                canCheckAddress={canCheckAddress}
                isCheckingAddress={isCheckingAddress}
                onCheckAddress={checkAddress}
              />
            </div>

            <div className="grid gap-3 @[48rem]/wire-in:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
              <DetailWell compact tone="amethyst">
                <div className="flex items-start gap-2">
                  <Package className="mt-0.5 size-4 shrink-0 text-interaction" />
                  <div className="min-w-0 space-y-1">
                    <Typography variant="eyebrow" tone="muted">
                      Products
                    </Typography>
                    <ProductList products={products} />
                  </div>
                </div>
              </DetailWell>

              <DetailWell compact className="space-y-4">
                <ContactRow icon={Phone} label="Recipient phone" value={recipientPhone} />
                <Info label="Card message" value={order.summary?.card_message} />
              </DetailWell>
            </div>

            {order.summary?.special_instructions || resendNote ? (
              <div className="grid gap-3 @[32rem]/wire-in:grid-cols-2">
                {order.summary?.special_instructions ? (
                  <Info label="Instructions" value={order.summary.special_instructions} />
                ) : null}
                {resendNote ? <Info label="Resend note" value={resendNote} /> : null}
              </div>
            ) : null}
          </div>

          {hasActions ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/50 p-5">
              {order.can_refuse ? (
                <Can I="respond" a="wire-in-orders">
                  <FsnRefuseOrderDialog
                    order={order}
                    refusalReasons={refusalReasons}
                    onRefused={onOrderHandled}
                  />
                </Can>
              ) : null}
              {order.sales_order_id ? (
                <Button asChild variant="outline" className="mr-auto order-first">
                  <Link
                    to="/$orgId/sales-orders/$salesOrderId"
                    params={{ orgId, salesOrderId: order.sales_order_id }}
                  >
                    Open order
                    <ArrowRight aria-hidden="true" />
                  </Link>
                </Button>
              ) : !isReadOnly && order.can_retry_import ? (
                <Button
                  onClick={() =>
                    mutateWithToast(
                      retryMutation,
                      { incomingOrderId: order.id },
                      { success: 'Order import retried.', error: 'Unable to retry order import.' }
                    )
                  }
                  disabled={retryMutation.isPending}
                >
                  {order.status === 'accepted' ? (
                    <PackageCheck className="h-4 w-4" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  {order.status === 'accepted' ? 'Import order' : 'Retry import'}
                </Button>
              ) : order.can_accept ? (
                <Can I="respond" a="wire-in-orders">
                  <FsnAcceptOrderDialog
                    order={order}
                    className="w-full @[32rem]/wire-in:w-auto"
                    onAccepted={onOrderHandled}
                  />
                </Can>
              ) : null}
            </div>
          ) : null}
        </>
      }
    />
  )
}

function WireInHeaderDescription({ messages, canCheckForNewOrders }) {
  return (
    <span className="block space-y-1">
      <span className="block">
        Review new Flower Shop Network orders and changes to existing orders.
        {canCheckForNewOrders ? (
          <>
            {' '}
            We check for new orders when you open this page and every few minutes. Still expecting
            one? Click{' '}
            <strong className="font-semibold text-foreground">Check for new orders</strong>.
          </>
        ) : null}
      </span>
      {messages.length > 0 ? <span className="block">{messages.join('; ')}</span> : null}
    </span>
  )
}

function StatusBadge({ status, fsnStatus = null }) {
  const handledOutcome = status === 'handled_in_fsn' ? handledInFsnPresentation(fsnStatus) : null
  const variant =
    status === 'pending'
      ? 'warning'
      : status === 'accepted' || status === 'imported'
        ? 'success'
        : status === 'import_failed' || status === 'refused'
          ? 'destructive'
          : handledOutcome?.variant === 'destructive'
            ? 'destructive'
            : 'muted'

  return <Badge variant={variant}>{handledOutcome?.label ?? statusLabel(status)}</Badge>
}

function DecisionDetail({
  icon: Icon = null,
  label,
  value,
  supportingValue = null,
  accent = 'neutral',
}) {
  return (
    <DetailWell compact tone={accent} className="min-w-0">
      <Typography variant="eyebrow" tone="muted">
        {label}
      </Typography>
      <Typography
        as="div"
        variant="body"
        weight="semibold"
        className="mt-2 flex min-w-0 items-center gap-2"
      >
        {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
        <span className="min-w-0 break-words">{value || 'Not provided'}</span>
      </Typography>
      {supportingValue ? (
        <Typography variant="supporting" className="mt-1 block">
          {supportingValue}
        </Typography>
      ) : null}
    </DetailWell>
  )
}

function AddressDecisionDetail({
  addressLines,
  mapsUrl,
  status,
  addressCheck,
  canCheckAddress,
  isCheckingAddress,
  onCheckAddress,
}) {
  const hasAddress = addressLines.length > 0
  const addressCheckBadges = getAddressCheckBadges(addressCheck)
  const matchedAddress = addressCheck?.address?.formatted_address
  const addressContent = hasAddress ? (
    <span className="space-y-0.5">
      {addressLines.map((line) => (
        <span key={line} className="block">
          {line}
        </span>
      ))}
    </span>
  ) : (
    'Not provided'
  )

  return (
    <DetailWell compact tone="periwinkle" className="@container/address min-w-0">
      <div className="flex items-start justify-between gap-2">
        <Typography variant="eyebrow" tone="muted">
          Delivery address
        </Typography>
        <div className="flex flex-wrap justify-end gap-1.5">
          {status ? <Badge className={status.className}>{status.label}</Badge> : null}
          {addressCheckBadges.map((badge) => (
            <Badge key={badge.label} className={badge.className}>
              {badge.label}
            </Badge>
          ))}
        </div>
      </div>
      <div className="mt-2 flex min-w-0 flex-col items-start gap-3 @[26rem]/address:flex-row @[26rem]/address:justify-between">
        <div className="flex min-w-0 items-start gap-2 text-sm font-medium">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          {mapsUrl && hasAddress ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 break-words rounded-sm text-foreground underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="Open recipient address in Google Maps"
            >
              {addressContent}
              <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                Open in Google Maps
                <ExternalLink className="h-3.5 w-3.5" />
              </span>
            </a>
          ) : (
            <span className="min-w-0 break-words">{addressContent}</span>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={onCheckAddress}
          disabled={!canCheckAddress || isCheckingAddress}
        >
          {isCheckingAddress ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MapPin className="h-4 w-4" />
          )}
          {addressCheck?.status === 'checked' ? 'Check again' : 'Check address'}
        </Button>
      </div>
      {matchedAddress ? (
        <Typography variant="supporting" className="mt-2 block break-words">
          Matched: {matchedAddress}
        </Typography>
      ) : null}
    </DetailWell>
  )
}

function ProductList({ products }) {
  if (products.length === 0) {
    return (
      <Typography variant="supporting">
        No products were included in the Flower Shop Network order.
      </Typography>
    )
  }

  return (
    <div className="divide-y divide-border/60">
      {products.map((product, index) => {
        const productName = getProductName(product)
        const description = getProductDescription(product, productName)
        const price = money(product.price)

        return (
          <div
            key={index}
            className="flex items-start justify-between gap-3 py-2 text-sm first:pt-0 last:pb-0"
          >
            <div className="min-w-0 space-y-1">
              <div className="whitespace-pre-line font-semibold">{productName}</div>
              {description ? (
                <p className="break-words leading-6 text-foreground">{description}</p>
              ) : null}
            </div>
            {price ? <div className="shrink-0 text-right font-medium">{price}</div> : null}
          </div>
        )
      })}
    </div>
  )
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0 space-y-1">
      <Typography variant="eyebrow" className="block">
        {label}
      </Typography>
      <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 break-words">{value || 'Not provided'}</span>
      </div>
    </div>
  )
}

function Info({ label, value }) {
  return (
    <div className="min-w-0 space-y-1">
      <Typography variant="eyebrow" className="block">
        {label}
      </Typography>
      <div className="break-words text-sm font-medium">{value || 'Not provided'}</div>
    </div>
  )
}

function money(value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null
  }

  return formatCurrency(value)
}

function formatDeliveryDate(value) {
  if (!value) {
    return null
  }

  const dateOnly = typeof value === 'string' ? value.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] : null
  const date = dateOnly ? dayjs(dateOnly, 'YYYY-MM-DD', true) : dayjs(value)

  return date.isValid() ? date.format('ddd, MMM D, YYYY') : String(value)
}

function formatRelativeDeliveryDate(value) {
  if (!value) {
    return null
  }

  const dateOnly = typeof value === 'string' ? value.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] : null
  const date = dateOnly ? dayjs(dateOnly, 'YYYY-MM-DD', true) : dayjs(value)

  if (!date.isValid()) {
    return null
  }

  const days = date.startOf('day').diff(dayjs().startOf('day'), 'day')

  if (days === 0) {
    return '(today)'
  }

  if (days === 1) {
    return '(tomorrow)'
  }

  if (days === -1) {
    return '(yesterday)'
  }

  const dayCount = dayCountWords[Math.abs(days)] ?? Math.abs(days)

  return days > 1 ? `(in ${dayCount} days)` : `(${dayCount} days ago)`
}

function getRecipientAddress(recipient = {}, order = {}) {
  return {
    line1: recipient.address1 || recipient.address,
    line2: recipient.address2,
    city: recipient.city || order.delivery_city,
    state: recipient.state || order.delivery_state,
    postal_code: recipient.zip,
    country: recipient.country || 'US',
  }
}

function formatRecipientAddressLines(address = {}) {
  const stateZip = [address.state, address.postal_code].filter(Boolean).join(' ')
  const cityLine = [address.city, stateZip].filter(Boolean).join(', ')

  return [address.line1, address.line2, cityLine].filter(Boolean)
}

function buildMapsUrl(address = {}) {
  const query = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ]
    .filter(Boolean)
    .join(', ')

  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : null
}

function getAddressStatus(address = {}) {
  const hasStreetCityState = Boolean(address.line1 && address.city && address.state)

  if (!hasStreetCityState) {
    return {
      label: 'Address incomplete',
      className:
        'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground',
    }
  }

  if (!address.postal_code) {
    return {
      label: 'ZIP missing',
      className:
        'border-status-warning-border bg-status-warning-background text-status-warning-foreground',
    }
  }

  return null
}

function getAddressCheckBadges(addressCheck) {
  if (!addressCheck) {
    return []
  }

  if (addressCheck.status === 'not_found') {
    return [
      {
        label: 'Could not verify',
        className: addressBadgeClass('red'),
      },
    ]
  }

  if (addressCheck.status === 'error') {
    return [
      {
        label: 'Check failed',
        className: addressBadgeClass('red'),
      },
    ]
  }

  if (addressCheck.status === 'incomplete') {
    return [
      {
        label: 'Address incomplete',
        className: addressBadgeClass('red'),
      },
    ]
  }

  const address = addressCheck.address ?? {}
  const serviceability = addressCheck.serviceability ?? {}
  const badges = [
    {
      label: address.is_rooftop ? 'Google verified' : 'Approximate match',
      className: addressBadgeClass(address.is_rooftop ? 'green' : 'amber'),
    },
  ]

  if (address.postal_code) {
    badges.push({
      label: `ZIP found: ${address.postal_code}`,
      className: addressBadgeClass('neutral'),
    })
  }

  if (serviceability.status === DeliveryServiceabilityStatus.Deliverable.Value) {
    badges.push({
      label: 'Likely in delivery area',
      className: addressBadgeClass('green'),
    })
  } else if (serviceability.status === DeliveryServiceabilityStatus.OutsideDeliveryArea.Value) {
    badges.push({
      label: 'Outside normal delivery area',
      className: addressBadgeClass('red'),
    })
  } else {
    badges.push({
      label: 'Delivery area unsure',
      className: addressBadgeClass('amber'),
    })
  }

  return badges
}

function addressBadgeClass(tone) {
  if (tone === 'green') {
    return 'border-status-success-border bg-status-success-background text-status-success-foreground dark:border-status-success-border dark:bg-status-success-background dark:text-status-success-foreground'
  }

  if (tone === 'red') {
    return 'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground dark:border-status-destructive-border dark:bg-status-destructive-background dark:text-status-destructive-foreground'
  }

  if (tone === 'amber') {
    return 'border-status-warning-border bg-status-warning-background text-status-warning-foreground dark:border-status-warning-border dark:bg-status-warning-background dark:text-status-warning-foreground'
  }

  return 'border-border/70 bg-background text-foreground dark:border-white/10 dark:bg-white/[0.04]'
}

function createDeliveryAddressFromRecipient(address = {}) {
  return {
    line1: address.line1 || '',
    line2: address.line2 || '',
    city: address.city || '',
    state: address.state || '',
    postal_code: address.postal_code || '',
    country: address.country || 'US',
  }
}

function formatRecipientPhone(order = {}, recipient = {}, formatPhone = (value) => value) {
  const phone = order.recipient_phone || recipient.phone

  return phone ? formatPhone(phone) : null
}

function uniqueContactMessages(orders = []) {
  return [
    ...new Set(
      orders
        .map((order) =>
          typeof order.summary?.contact_message === 'string'
            ? order.summary.contact_message.trim()
            : ''
        )
        .filter((message) => message)
    ),
  ]
}

function getProductName(product = {}) {
  if (!product) {
    return 'No product listed'
  }

  return formatLineItemName(
    product.name || product.fsnProductData?.name || product.description || 'Item'
  )
}

function getProductDescription(product = {}, productName = '') {
  if (!product) {
    return null
  }

  const description =
    product.description || product.fsnProductData?.recipe || product.fsnProductData?.description

  if (!description || description === productName) {
    return null
  }

  return description
}

function statusLabel(status) {
  const labels = {
    accepted: 'Accepted in FSN',
    handled_in_fsn: 'Handled in FSN',
    import_failed: 'Import Failed',
    imported: 'Imported',
    pending: 'Pending',
    refused: 'Refused',
  }

  if (labels[status]) {
    return labels[status]
  }

  return String(status || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function handledInFsnPresentation(fsnStatus) {
  const normalizedStatus = String(fsnStatus ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

  if (normalizedStatus === 'refused') {
    return { label: 'Refused in FSN', variant: 'destructive' }
  }

  if (['rejected', 'declined'].includes(normalizedStatus)) {
    return { label: 'Rejected in FSN', variant: 'destructive' }
  }

  if (['cancelled', 'canceled'].includes(normalizedStatus)) {
    return { label: 'Cancelled in FSN', variant: 'destructive' }
  }

  if (normalizedStatus === 'complete') {
    return { label: 'Completed in FSN', variant: 'muted' }
  }

  return { label: 'Handled in FSN', variant: 'muted' }
}

/** The page's orders with ones handled on this visit put back where they were; the server's copy wins when listed. */
function keepHandledInPlace(orders, handledOrders) {
  const visible = [...orders]
  for (const { order, index } of [...handledOrders].sort((a, b) => a.index - b.index)) {
    if (!visible.some((listed) => listed.id === order.id)) {
      visible.splice(Math.min(index, visible.length), 0, order)
    }
  }
  return visible
}

function wireInOrderCountText(meta = {}, fallbackCount = 0) {
  const total = Number(meta.total ?? fallbackCount ?? 0)
  const pageSize = Number(meta.page_size ?? WIRE_IN_PAGE_SIZE)
  const currentPage = Number(meta.current_page ?? 1)

  if (total === 0) {
    return '0 orders shown'
  }

  const start = (currentPage - 1) * pageSize + 1
  const visibleCount = Number(fallbackCount ?? 0)

  if (visibleCount <= 0) {
    return '0 orders shown'
  }

  const end = Math.min(total, start + visibleCount - 1)

  return `Showing ${start}-${end} of ${total} order${total === 1 ? '' : 's'}`
}

function WireInPagination({ meta = {}, onPageChange }) {
  const currentPage = Number(meta.current_page ?? 1)
  const lastPage = Number(meta.last_page ?? 1)
  const displayedLastPage = Math.max(lastPage, currentPage)

  if (lastPage <= 1 && currentPage <= 1) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Typography variant="supporting">
        Page {currentPage} of {displayedLastPage}
      </Typography>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
      >
        <ChevronLeft className="h-4 w-4" />
        Previous
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={currentPage >= lastPage}
        onClick={() => onPageChange(Math.min(lastPage, currentPage + 1))}
      >
        Next
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}
